/**
 * The two plugins this one codebase publishes.
 *
 * Figma's Community is searched by name, and someone hunting for an EPS
 * importer and someone hunting for an Illustrator importer are running two
 * different searches — neither finds a listing named for the other format. So
 * there are two published plugins, each with its own manifest id, its own
 * listing and its own domain.
 *
 * What there isn't is two copies of the code. That was tried: the apps drifted,
 * every fix had to be made twice, and the engines were byte-identical anyway
 * because the converter branches on the file's first few bytes, not its suffix.
 * Everything that genuinely differs between the two plugins is in
 * `variants.json` — a name, a blurb, a brand string, a domain — and everything
 * else is shared, down to a single deploy.
 *
 * Both accept `.ai` and `.eps`; the ordering in `accept` and the wording of
 * `dropHint` just lead with the format the plugin is named for. Rejecting a file
 * the engine handles perfectly would be a worse experience than a slightly
 * broader listing.
 *
 * Two consumers read this table, which is why the data lives in JSON rather
 * than here: `esbuild.plugin.mjs` (builds one sandbox bundle and one manifest
 * per variant) and this module (the app code).
 */

import table from '../variants.json';
import { defineVariants, type Variant, type VariantTable } from '@figma-importers/variants';

export type { Variant };

/**
 * Which variant a sandbox bundle was built for, injected by esbuild's `define`
 * (see esbuild.plugin.mjs). Declared here rather than in globals.d.ts because
 * this is the only module that reads it — and because the plugin tsconfig
 * doesn't pull in the UI's ambient declarations.
 */
declare const __VARIANT_KEY__: string;

/**
 * The Cloudflare Pages project every plugin here is built and deployed by. Each
 * variant's `ui` is a custom domain attached to *this one* project, so there is
 * still a single build and a single deploy behind all of them.
 */
export const PAGES_PROJECT: string = (table as VariantTable).pagesProject!;

/**
 * The table and its lookups come from `@figma-importers/variants`, which every
 * importer now publishes its listings through; this module keeps the names the
 * rest of this app has always imported. Under the test runner there is no
 * define at all, hence the `typeof` guard — reading an undeclared identifier
 * directly would throw, while `typeof` on one returns "undefined".
 */
const defined = defineVariants(table as VariantTable, typeof __VARIANT_KEY__ === 'string' ? __VARIANT_KEY__ : undefined);

export const VARIANTS: Variant[] = defined.VARIANTS;
export const DEFAULT_VARIANT: Variant = defined.DEFAULT_VARIANT;
export const ACTIVE_VARIANT: Variant = defined.ACTIVE_VARIANT;
export const variantById = defined.variantById;
export const variantByKey = defined.variantByKey;
